import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, RefreshCw, ClipboardPaste } from 'lucide-react'
import { screenshotUrl, startVnc, stopVnc } from '../api'
import type { CampaignStats } from '../api'

const TOUCH_CURSOR_CSS = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABcAAAAXCAQAAABKIxwrAAAA1UlEQVR4XpWUMaqEMBRFY6+FhXvQ3i7tVMK4g0y8DHZxdmUxa7NwCecj/CKEAXmc9iRcXu6LI6eio8czE5jx9HRUuZHLDRMqYaL5pQ8EdGiP2zKudRrXbdnjIURgKPUHQt9Xm9wnp03fF0I8cn1AZ3y+MzHj+T4jYvjXaQiokIsDiEBz6RXTFaNQCq5ITFSODh0qM5e06RCic/Roj+5zxx4RvcOjbbnXtwXhHTMa13t9XBGzI6A63et1QgTj7cbsxskY5258VVNnTI009d2+TfZdNf4EfwHXo4ZtrZHBAAAAAElFTkSuQmCC") 11 11, none`

const CAPTCHA_LABELS: Record<string, string> = {
  'CAPTCHA: Обнаружена CheckBox': 'CheckBox',
  'CAPTCHA: Капча усложнена, SmartCaptcha IMG': 'SmartCaptcha IMG',
  'CAPTCHA: Капча усложнена, TextCaptcha': 'TextCaptcha',
  'CAPTCHA: Капча усложнена, SliderCaptcha': 'SliderCaptcha',
  'CAPTCHA: Эскалация → SliderCaptcha': 'SliderCaptcha',
  'CAPTCHA: Эскалация → SmartCaptcha IMG': 'SmartCaptcha IMG',
  'CAPTCHA: Эскалация → TextCaptcha': 'TextCaptcha',
  'Обнаружили SmartCaptcha IMG': 'SmartCaptcha IMG',
  'Обнаружили CheckBox': 'CheckBox',
  'Обнаружили TXT': 'TextCaptcha',
  'Обнаружили SliderCaptcha': 'SliderCaptcha',
  'Яндекс Капча': 'SmartCaptcha',
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 19) return `${n} ${many}`
  if (mod10 === 1) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${few}`
  return `${n} ${many}`
}

function formatDurationRu(totalSec: number): string {
  if (!totalSec || totalSec < 60) return '< 1 минуты'
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const parts: string[] = []
  if (h > 0) parts.push(pluralRu(h, 'час', 'часа', 'часов'))
  if (m > 0) parts.push(pluralRu(m, 'минута', 'минуты', 'минут'))
  return parts.join(' ')
}

function formatElapsed(sec: number): string {
  if (!sec) return '—'
  return formatDurationRu(sec)
}

function formatUptime(sec: number): string {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m < 60 ? `${m}m ${s}s` : `${Math.floor(m / 60)}h ${m % 60}m`
}

function formatStartedAt(ts: number): string {
  const elapsed = Math.max(0, Math.floor(Date.now() / 1000 - ts))
  return formatDurationRu(elapsed)
}

function workerPhase(state: string): 'active' | 'starting' | 'idle' {
  if (state === 'running') return 'active'
  if (state === 'launching' || state === 'claiming' || state === 'fingerprint' || state === 'proxy' || state === 'task_fetch') return 'starting'
  // Профайлерские warming-состояния — это активная работа сценария (Serp Tune):
  // воркер уже в браузере, ходит по Yandex, кликает попапы, эмулирует поведение.
  if (state === 'warming_init' || state === 'serp_load' || state === 'popups_dismiss' ||
      state === 'idle_emulation' || state === 'nav_click') return 'active'
  // Ручное управление (кубик manual_pause): браузер ЖИВОЙ и ждёт оператора —
  // плитка должна быть кликабельной, чтобы открыть VNC и порулить руками.
  if (state === 'manual_vnc') return 'active'
  return 'idle'
}

const STATE_LABELS: Record<string, string> = {
  running: 'Работает',
  launching: 'Запуск...',
  claiming: 'Получение профиля...',
  task_fetch: 'Загрузка задания...',
  fingerprint: 'Отпечаток...',
  proxy: 'Подключение прокси...',
  pause: 'Пауза',
  idle: 'Пауза',
  closing: 'Закрытие...',
  stopped: 'Остановлен',
  unknown: 'Ожидание...',
  completed: 'Завершён',
  waiting_profile: 'Ожидание профилей...',
  waiting_db: 'Ожидание БД...',
  serp_fail_paused: '⏸ Пауза: выдача не загрузилась (VNC)',
  // Профайлерские warming-состояния (scenarios/warming.py → _state_writer)
  warming_init: 'Получение кук',
  serp_load: 'Загрузка SERP',
  popups_dismiss: 'Закрытие попапов',
  idle_emulation: 'Эмуляция поведения',
  nav_click: 'Клик HeaderNav',
  manual_vnc: '🖐 Ручное управление (VNC)',
}

export const ResultsStats = memo(function ResultsStats({ stats, mode = 'booster' }: { stats: any; mode?: 'booster' | 'profiler' }) {
  const ok = stats.ok ?? 0
  const fail = stats.fail ?? 0
  const skip = stats.skip ?? 0  // Idea #69: SmartPilot skips
  const total = ok + fail
  const avgPos = stats.avg_position ?? 0
  const speed = stats.speed_per_hour ?? 0
  const elapsed = stats.elapsed_sec ?? 0
  const captcha = stats.captcha ?? 0
  const captchaTypes: Record<string, number> = stats.captcha_types ?? {}
  const error429 = stats.error_429 ?? 0
  const totalTasks = stats.total_tasks ?? 0
  const remainingTasks = stats.remaining_tasks ?? 0
  const isProfiler = mode === 'profiler'
  // Per-iteration timing (profiler only) — avg/min/max seconds.
  const iterAvg = stats.iter_avg_sec ?? 0
  const iterMin = stats.iter_min_sec ?? 0
  const iterMax = stats.iter_max_sec ?? 0

  const pct = total > 0 ? Math.round(ok / total * 100) : 0
  const tasksPct = totalTasks > 0 ? Math.round(((totalTasks - remainingTasks) / totalTasks) * 100) : 0

  // For the profiler, the SERP "AVG позиция" is meaningless (it never clicks a
  // result). Show the mean per-iteration warm time instead (min/max in caption),
  // and frame "Скорость" as прогревов/час.
  const positionOrTimeCard = isProfiler
    ? {
        title: 'Ср. время итерации',
        value: iterAvg > 0 ? formatElapsed(Math.round(iterAvg)) : '—',
        description: iterAvg > 0 && iterMax > 0
          ? `мин ${formatElapsed(iterMin)} · макс ${formatElapsed(iterMax)}`
          : 'на прокачку',
      }
    : { title: 'AVG позиция', value: avgPos > 0 ? avgPos : '—', description: 'без бренда' }

  const speedCard = isProfiler
    ? { title: 'Прогревов/ч', value: speed > 0 ? speed : '—', description: speed > 0 ? `≈${(speed * 24).toLocaleString('ru-RU')}/сут` : '—' }
    : { title: 'Скорость', value: speed > 0 ? speed : '—', description: speed > 0 ? `/час · ≈${(speed * 24).toLocaleString('ru-RU')}/сут` : '—' }

  const cards = [
    { title: 'Выполнений', value: total, description: elapsed > 0 ? formatElapsed(elapsed) : '—' },
    { title: 'Успешно', value: ok, description: total > 0 ? `${pct}%` : '—' },
    { title: 'Ошибок', value: fail, description: total > 0 ? `${100 - pct}%` : '—' },
    positionOrTimeCard,
    speedCard,
    { title: 'Капча', value: captcha, description: `${Object.keys(captchaTypes).length} типов` },
    ...(!isProfiler && skip > 0 ? [{
      title: 'Пропуски',
      value: skip.toLocaleString('ru-RU'),
      description: 'SmartPilot',
    }] : []),
    ...(totalTasks > 0 ? [{
      title: 'Осталось',
      value: totalTasks === -1 ? '∞' : remainingTasks.toLocaleString('ru-RU'),
      description: totalTasks === -1 ? 'unlimit' : `${tasksPct}% выполнено`,
    }] : []),
  ]

  return (
    <div data-no-magic className="mb-5 space-y-3 animate-fade-in">
      <div className="border border-border rounded-lg overflow-hidden bg-muted/30">
        <div className="grid grid-cols-3 lg:grid-cols-6">
          {cards.map((c, i) => (
            <div
              key={c.title}
              className={`px-3 py-3 md:px-4 md:py-4 min-w-0 ${
                i % 3 !== 0 ? 'border-l border-border' : ''
              } ${i >= 3 ? 'border-t border-border lg:border-t-0' : ''} ${
                i >= 1 && i <= 2 ? '' : ''
              } ${i >= 3 ? 'lg:border-l' : ''}`}
            >
              <p className="text-[10px] font-medium text-muted-foreground mb-1 md:text-xs">{c.title}</p>
              <div className="text-xl font-bold tabular-nums tracking-tight md:text-2xl">{c.value}</div>
              <p className="text-[10px] text-muted-foreground mt-0.5 md:text-xs">{c.description}</p>
            </div>
          ))}
        </div>
        {(captcha > 0 || error429 > 0) && (
          <div className="border-t border-border px-3 py-2.5 md:px-4 md:py-3 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
            {captcha > 0 && Object.keys(captchaTypes).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 md:text-[11px] mr-1">Капча</span>
                {Object.entries(captchaTypes).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                  <div key={name} className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-muted/40 text-[11px]">
                    <span className="text-muted-foreground truncate">{CAPTCHA_LABELS[name] || name}</span>
                    <span className="tabular-nums font-bold text-foreground">{count}</span>
                  </div>
                ))}
              </div>
            )}
            {error429 > 0 && (
              <div className={`flex items-center gap-2 shrink-0 ${captcha > 0 ? 'md:ml-auto' : ''}`}>
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-500/10 text-[11px]">
                  <span className="text-orange-400">Error 429</span>
                  <span className="tabular-nums font-bold text-orange-300">{error429}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

const INACTIVE_STATES = new Set(['idle', 'pause', 'closing', 'stopped', 'claiming', 'task_fetch', 'fingerprint', 'proxy', 'launching', 'unknown', 'completed', 'waiting_profile', 'waiting_db'])
const PADDING = 4
const TITLEBAR_H = 26
const VNC_SCALE = 0.85
const MAX_W = 480

function VncViewer({ workerId, display, browserState, onClose }: {
  workerId: number; display: string; browserState: string; onClose: () => void
}) {
  // Multi-campaign safe: server-side VNC, screenshot and WS endpoints
  // are now keyed by absolute Xvfb display number (e.g. 149), not by
  // worker_id (which collides between concurrent campaigns). Parse it
  // out of the `display` string (":149" → 149) and use it for every
  // backend call. `workerId` stays only for the visible "W2" label.
  const displayNum = useMemo(() => {
    const m = /^:?(\d+)$/.exec((display || '').trim())
    return m ? parseInt(m[1], 10) : (99 + workerId)
  }, [display, workerId])
  const inactive = INACTIVE_STATES.has(browserState)
  const [mode, setMode] = useState<'loading' | 'vnc' | 'screenshot' | 'error'>('loading')
  const [interactive, setInteractive] = useState(false)
  const [screenW, setScreenW] = useState(385)
  const [screenH, setScreenH] = useState(854)
  const [xvfbW, setXvfbW] = useState(398)
  const [xvfbH, setXvfbH] = useState(815)
  const [errMsg, setErrMsg] = useState('')
  const closedRef = useRef(false)
  const [disconnected, setDisconnected] = useState(false)
  const [imgUrl, setImgUrl] = useState('')
  const [imgLoading, setImgLoading] = useState(false)
  const [viewportW, setViewportW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024))
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevStateRef = useRef(browserState)
  const preferVnc = useRef(true)
  const rfbRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intentionalRef = useRef(false)
  // Окно-вставка (fallback, когда navigator.clipboard недоступен — http/нет прав).
  const [pasteOpen, setPasteOpen] = useState(false)
  const pasteRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const layout = useMemo(() => {
    const ratio = xvfbW / xvfbH
    const sidePad = 16
    const maxContentW = Math.min(MAX_W, Math.max(120, viewportW - sidePad - PADDING * 2))
    let h = window.innerHeight * VNC_SCALE - TITLEBAR_H - PADDING * 2
    let w = h * ratio
    if (w > maxContentW) { w = maxContentW; h = w / ratio }
    return {
      frameW: Math.round(w + PADDING * 2),
      frameH: Math.round(h + TITLEBAR_H + PADDING * 2),
      contentW: Math.round(w),
      contentH: Math.round(h),
      scale: w / xvfbW,
    }
  }, [xvfbW, xvfbH, viewportW])

  const applyDims = (r: any) => {
    if (r.screen_w) setScreenW(r.screen_w)
    if (r.screen_h) setScreenH(r.screen_h)
    if (r.xvfb_w) setXvfbW(r.xvfb_w)
    if (r.xvfb_h) setXvfbH(r.xvfb_h)
  }

  const destroyRfb = useCallback(() => {
    intentionalRef.current = true
    try { rfbRef.current?.disconnect() } catch {}
    rfbRef.current = null
  }, [])

  const connectRfb = useCallback((pw: string, viewOnly: boolean) => {
    destroyRfb()
    const el = containerRef.current
    if (!el) return
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${proto}://${location.hostname}${location.port ? ':' + location.port : ''}/ws/vnc/${displayNum}`
    // Bug #118: retry VNC module load once — network hiccups or stale
    // cache on client VPS can cause the lazy chunk to fail to load.
    const loadRfb = () => import('@novnc/novnc/lib/rfb.js')
    loadRfb().catch(() => new Promise(r => setTimeout(r, 2000)).then(loadRfb)).then(mod => {
      if (closedRef.current) return
      const RFB = mod.default
      const rfb = new RFB(el, url, { credentials: pw ? { password: pw } : undefined, wsProtocols: ['binary'] })
      rfb.viewOnly = viewOnly
      rfb.scaleViewport = true
      rfb.resizeSession = false
      rfb.clipViewport = true
      rfb.addEventListener('connect', () => {
        if (!closedRef.current) { setDisconnected(false); setMode('vnc') }
      })
      rfb.addEventListener('disconnect', () => {
        if (closedRef.current) return
        if (intentionalRef.current) { intentionalRef.current = false; return }
        setDisconnected(true)
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = setTimeout(() => {
          if (!closedRef.current && preferVnc.current) reconnect()
        }, 1500)
      })
      // Буфер VNC → Mac: копирование в удалённом браузере (Ctrl+C) → x11vnc шлёт
      // ServerCutText → noVNC событие 'clipboard' → кладём в локальный буфер Mac.
      // Требует secure context (https) для navigator.clipboard.writeText.
      rfb.addEventListener('clipboard', (e: any) => {
        const t = e?.detail?.text
        if (t && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(t).catch(() => { /* нет прав / insecure http */ })
        }
      })
      rfbRef.current = rfb
    }).catch(() => {
      if (!closedRef.current) { setErrMsg('Failed to load VNC module'); setMode('error') }
    })
  }, [displayNum])

  useEffect(() => {
    closedRef.current = false
    let cancelled = false
    ;(async () => {
      const res = await startVnc(displayNum)
      if (cancelled || closedRef.current) return
      applyDims(res)
      if (res.vnc_port) {
        setInteractive(!!res.interactive)
        connectRfb(res.vnc_pw || '', !res.interactive)
      } else {
        setErrMsg(res.error || 'Трансляция недоступна')
        setImgUrl(screenshotUrl(displayNum))
        setMode('screenshot')
      }
    })()
    return () => { cancelled = true; destroyRfb() }
  }, [displayNum, connectRfb, destroyRfb])

  const handleClose = useCallback(() => {
    closedRef.current = true
    destroyRfb()
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    stopVnc(displayNum)
    onClose()
  }, [displayNum, onClose, destroyRfb])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleClose])

  const refreshScreenshot = useCallback(() => {
    setImgLoading(true)
    setImgUrl(screenshotUrl(displayNum))
  }, [displayNum])

  useEffect(() => {
    if (mode !== 'screenshot') return
    intervalRef.current = setInterval(refreshScreenshot, 2000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [mode, refreshScreenshot])

  const switchToScreenshot = useCallback(() => {
    preferVnc.current = false
    destroyRfb()
    stopVnc(displayNum)
    setImgUrl(screenshotUrl(displayNum))
    setMode('screenshot')
  }, [displayNum, destroyRfb])

  const reconnect = useCallback(async () => {
    preferVnc.current = true
    destroyRfb()
    if (intervalRef.current) clearInterval(intervalRef.current)
    setDisconnected(false)
    setMode('loading')
    const res = await startVnc(displayNum)
    if (closedRef.current) return
    applyDims(res)
    if (res.vnc_port) {
      setInteractive(!!res.interactive)
      connectRfb(res.vnc_pw || '', !res.interactive)
    } else {
      setErrMsg(res.error || 'Трансляция недоступна')
      setImgUrl(screenshotUrl(displayNum))
      setMode('screenshot')
    }
  }, [displayNum, connectRfb, destroyRfb])

  useEffect(() => {
    const prev = prevStateRef.current
    prevStateRef.current = browserState
    if (browserState === 'running' && prev !== 'running' && !closedRef.current && preferVnc.current && mode !== 'vnc') {
      setMode('loading')
      const t = setTimeout(() => { if (!closedRef.current) reconnect() }, 1200)
      return () => clearTimeout(t)
    }
  }, [browserState, reconnect, mode])

  // Отправить текст в СФОКУСИРОВАННОЕ поле удалённого браузера: печатаем его
  // посимвольно через sendKey — это НАДЁЖНО и НЕ зависит от буфера удалённого X
  // и обработки Ctrl+V (которые могут не сработать). Дополнительно кладём текст в
  // буфер remote (clipboardPasteFrom), чтобы работал и ручной Ctrl+V. Unicode —
  // через -xkb/-add_keysyms в x11vnc.
  const writeToRemote = useCallback((text: string) => {
    const rfb = rfbRef.current
    if (!rfb || !text) return
    try { rfb.clipboardPasteFrom(text) } catch { /* для ручного Ctrl+V */ }
    for (const ch of text) {
      const cp = ch.codePointAt(0) || 0
      let keysym: number
      if (ch === '\n' || ch === '\r') keysym = 0xff0d       // Return
      else if (ch === '\t') keysym = 0xff09                 // Tab
      else if (cp < 0x100) keysym = cp                      // Latin-1 (ASCII)
      else keysym = 0x01000000 + cp                         // Unicode keysym
      try {
        rfb.sendKey(keysym, null, true)
        rfb.sendKey(keysym, null, false)
      } catch { /* sendKey недоступен */ }
    }
  }, [])

  const pasteToVnc = useCallback(async () => {
    // 1) Прямое чтение буфера Mac (нужен https + разрешение) → печать в поле.
    try {
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText()
        if (text) { writeToRemote(text); return }
      }
    } catch { /* нет прав / insecure http — уходим в окно-вставку ниже */ }
    // 2) Fallback: окно-вставка. Нативный ⌘V в textarea работает ВЕЗДЕ (в т.ч.
    //    http, без clipboard API) — оттуда печатаем в удалённое поле.
    setPasteOpen(true)
    setTimeout(() => pasteRef.current?.focus(), 30)
  }, [writeToRemote])

  const submitPasteBox = useCallback(() => {
    const t = pasteRef.current?.value || ''
    setPasteOpen(false)
    if (t) writeToRemote(t)
  }, [writeToRemote])

  // Ремап Mac-комбинаций (⌘) в удалённые Ctrl-комбинации внутри VNC: ⌘A/⌘C/⌘V/
  // ⌘X/⌘Z/⌘Y/⌘⇧Z → Ctrl+… в Linux-браузере (выделить всё / копировать / вставить
  // / вырезать / отменить / повторить). На Mac это Cmd(⌘), а remote-браузер ждёт
  // Ctrl — без ремапа ⌘A/⌘C не работают. ⌘V дополнительно синкает буфер Mac→remote
  // и жмёт Ctrl+V (см. pasteToVnc). Настоящий Ctrl с внешней клавиатуры noVNC
  // прокидывает сам — не трогаем. Немапленные ⌘-комбо (⌘W/⌘R/⌘Tab) отдаём Mac.
  // capture=true — перехватываем ДО noVNC, чтобы в remote не ушёл лишний Meta+key.
  useEffect(() => {
    // Пока открыто окно-вставка — НЕ перехватываем комбинации: пользователь
    // работает с нативной textarea (⌘V/⌘A/⌘C в ней должны работать штатно).
    if (mode !== 'vnc' || !interactive || pasteOpen) return
    const CTRL = 0xffe3
    const COMBO: Record<string, [number, string]> = {
      a: [0x61, 'KeyA'], c: [0x63, 'KeyC'], v: [0x76, 'KeyV'],
      x: [0x78, 'KeyX'], z: [0x7a, 'KeyZ'], y: [0x79, 'KeyY'],
    }
    const handler = (e: KeyboardEvent) => {
      // Только Cmd(⌘) без Ctrl/Alt. Реальный Ctrl noVNC прокидывает сам.
      if (!e.metaKey || e.ctrlKey || e.altKey) return
      let k = e.key.toLowerCase()
      if (k === 'z' && e.shiftKey) k = 'y'  // ⌘⇧Z (redo на Mac) → Ctrl+Y
      const combo = COMBO[k]
      if (!combo) return  // немапленные ⌘-комбо → Mac-браузеру
      e.preventDefault(); e.stopPropagation()
      if (k === 'v') { pasteToVnc(); return }  // синк буфера Mac→remote + Ctrl+V
      const rfb = rfbRef.current
      if (!rfb) return
      try {
        rfb.sendKey(CTRL, 'ControlLeft', true)
        rfb.sendKey(combo[0], combo[1], true)
        rfb.sendKey(combo[0], combo[1], false)
        rfb.sendKey(CTRL, 'ControlLeft', false)
      } catch { /* sendKey недоступен в этой сборке noVNC */ }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [mode, interactive, pasteToVnc, pasteOpen])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-2 md:px-0" onClick={handleClose}>
      <div className="relative flex flex-col animate-slide-up max-w-[calc(100vw-1rem)] md:max-w-none" style={{ width: layout.frameW, height: layout.frameH }} onClick={e => e.stopPropagation()}>
        <div className="flex flex-col h-full rounded-2xl shadow-2xl overflow-hidden bg-card border-2 border-border">
          <div className="flex items-center justify-between px-3 shrink-0 bg-muted border-b border-border" style={{ height: TITLEBAR_H }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-semibold text-foreground">W{workerId + 1}</span>
              <span className="text-[9px] font-mono truncate text-muted-foreground">{screenW}×{screenH}</span>
              {inactive
                ? <span className="text-[9px] text-muted-foreground">{STATE_LABELS[browserState] || browserState}</span>
                : mode === 'vnc' ? <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 pulse-glow" />
                : mode === 'screenshot' ? <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" /> : null}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {mode === 'screenshot' && <Button variant="ghost" size="sm" onClick={reconnect} className="h-5 px-1.5 text-[9px] text-blue-400 hover:text-blue-300">LIVE</Button>}
              {mode === 'vnc' && <>
                {interactive && <Button variant="ghost" size="icon" onClick={pasteToVnc} className="h-5 w-5 text-muted-foreground hover:text-emerald-400" title="Вставить из буфера (Ctrl+V)"><ClipboardPaste className="w-3 h-3" /></Button>}
                <Button variant="ghost" size="icon" onClick={reconnect} className="h-5 w-5 text-muted-foreground hover:text-blue-400" title="Переподключить"><RefreshCw className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={switchToScreenshot} className="h-5 px-1.5 text-[9px] text-muted-foreground">IMG</Button>
              </>}
              <Button variant="ghost" size="icon" onClick={handleClose} className="h-5 w-5 text-sm text-muted-foreground">×</Button>
            </div>
          </div>

          <div className="relative bg-black overflow-hidden" style={{ width: layout.contentW, height: layout.contentH, cursor: interactive && mode === 'vnc' ? TOUCH_CURSOR_CSS : undefined }}>
            {/* noVNC renders into this container */}
            <div ref={containerRef} style={{ width: layout.contentW, height: layout.contentH, position: 'absolute', top: 0, left: 0 }} />
            {pasteOpen && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/85 backdrop-blur-sm p-4" onClick={e => e.stopPropagation()}>
                <div className="text-[11px] text-foreground/90 text-center leading-snug">
                  Вставьте данные сюда (<b>⌘V</b>) и нажмите «Вставить в браузер».<br />
                  Текст будет напечатан в поле, которое сейчас в фокусе в удалённом браузере.
                </div>
                <textarea ref={pasteRef} rows={4} autoFocus
                  className="w-full max-w-md text-xs rounded-md bg-background border border-border p-2 text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="⌘V сюда…"
                  onKeyDown={e => {
                    if (e.key === 'Escape') { e.preventDefault(); setPasteOpen(false) }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitPasteBox() }
                  }} />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="h-7 text-xs" onClick={submitPasteBox}>Вставить в браузер</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPasteOpen(false)}>Отмена</Button>
                </div>
              </div>
            )}
            {mode === 'loading' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-muted-foreground">Подключение...</span>
              </div>
            )}
            {disconnected && mode === 'vnc' && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                <span className="text-xs text-muted-foreground">Переподключение...</span>
              </div>
            )}
            {mode === 'screenshot' && (
              <div className="w-full h-full flex items-start justify-center overflow-hidden">
                {imgUrl
                  ? <img src={imgUrl} alt={`W${workerId + 1}`} className={`w-full h-full object-contain transition-opacity duration-150 ${imgLoading ? 'opacity-40' : 'opacity-100'}`} onLoad={() => setImgLoading(false)} onError={() => { setImgUrl(''); setMode('error') }} draggable={false} />
                  : <span className="text-xs mt-20 text-muted-foreground/50">Loading...</span>}
              </div>
            )}
            {mode === 'error' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span className="text-xs text-muted-foreground">{errMsg || 'Display unavailable'}</span>
                <Button variant="secondary" size="sm" onClick={reconnect} className="text-xs">Retry</Button>
              </div>
            )}
            {inactive && mode !== 'loading' && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300">
                <div className="text-sm font-medium mb-1 text-foreground">
                  {browserState === 'idle' || browserState === 'pause' ? 'Пауза' : browserState === 'claiming' ? 'Получение профиля...' : browserState === 'task_fetch' ? 'Загрузка задания...' : browserState === 'fingerprint' ? 'Отпечаток...' : browserState === 'proxy' ? 'Подключение прокси...' : browserState === 'launching' ? 'Запуск браузера...' : browserState === 'closing' ? 'Закрытие браузера...' : browserState === 'completed' ? 'Завершён' : browserState === 'waiting_profile' ? 'Ожидание профилей...' : browserState === 'waiting_db' ? 'Ожидание БД...' : 'Ожидание...'}
                </div>
                <div className="text-xs text-muted-foreground">Браузер не активен</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function WorkersTable({ workers, startedAt }: { workers: any[]; startedAt?: number }) {
  const [selectedWorker, setSelectedWorker] = useState<any>(null)
  // Collapsed by default on mobile (< md breakpoint, 768px)
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  )
  const [, setTick] = useState(0)

  const alive = workers.filter(w => w.alive)
  const dead = workers.length - alive.length
  const active = alive.filter(w => workerPhase(w.browser_state) === 'active').length
  const waiting = alive.length - active

  useEffect(() => {
    if (!startedAt) return
    const iv = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(iv)
  }, [startedAt])

  useEffect(() => {
    if (selectedWorker && !workers.find(w => w.worker_id === selectedWorker.worker_id && w.alive))
      setSelectedWorker(null)
  }, [workers, selectedWorker])

  if (alive.length === 0 && dead === 0) return null

  return (
    <div>
      <div className="flex flex-col gap-2 mb-4 min-w-0 md:flex-row md:flex-wrap md:items-center md:gap-2.5">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 shrink-0 md:gap-2">
            <h3 className="text-xs font-semibold text-foreground md:text-sm">Потоки</h3>
            <span className="inline-flex items-center h-5 px-1.5 text-[10px] font-bold tabular-nums rounded-md bg-muted border border-border/50 text-foreground md:h-[22px] md:px-2 md:text-[11px]">
              {active}<span className="text-muted-foreground/50 mx-0.5">/</span>{workers.length}
            </span>
          </div>

          {startedAt ? (
            <div className="flex items-center gap-1 px-1.5 h-5 min-w-0 rounded-md bg-muted/60 border border-border/40 md:gap-1.5 md:px-2 md:h-[22px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted-foreground/60 md:w-[11px] md:h-[11px]">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="hidden text-[10px] text-muted-foreground/60 md:inline md:text-[11px]">Время работы:</span>
              <span className="text-[10px] tabular-nums font-medium text-muted-foreground truncate md:text-[11px]">{formatStartedAt(startedAt)}</span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1 min-w-0 md:gap-1.5">
          {active > 0 && (
            <span className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 md:gap-1 md:h-[22px] md:px-2 md:text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="truncate">{active} в работе</span>
            </span>
          )}
          {waiting > 0 && (
            <span className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded-md bg-muted text-muted-foreground border border-border/40 md:gap-1 md:h-[22px] md:px-2 md:text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
              <span className="truncate">{waiting} ожидание</span>
            </span>
          )}
          {dead > 0 && (
            <span className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded-md bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 md:gap-1 md:h-[22px] md:px-2 md:text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500/60 shrink-0" />
              <span className="truncate">{dead} остановлено</span>
            </span>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)}
          className="h-6 self-start text-[10px] text-muted-foreground/60 hover:text-muted-foreground gap-1 px-1.5 md:ml-auto md:self-center md:text-[11px] md:px-2">
          {collapsed
            ? <><Eye className="w-3 h-3 shrink-0" /> Показать потоки</>
            : <><EyeOff className="w-3 h-3 shrink-0" /> Скрыть потоки</>}
        </Button>
      </div>

      {collapsed ? null : alive.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {alive.map(w => {
            const phase = workerPhase(w.browser_state)
            const isActive = phase === 'active'
            const isStarting = phase === 'starting'
            const dot = isActive ? 'bg-emerald-400 pulse-glow' : isStarting ? 'bg-amber-400 animate-pulse' : 'bg-muted-foreground'
            return (
              <Card key={w.worker_id}
                onClick={() => isActive ? setSelectedWorker(w) : null}
                className={`${isActive ? 'cursor-pointer hover:shadow-md hover:border-primary/40' : 'opacity-60'} transition-all duration-150 animate-fade-in ${isActive ? 'border-green-500/30 bg-green-500/5' : isStarting ? 'border-amber-500/20 bg-amber-500/5' : ''}`}>
                <CardContent className="px-2 py-2 text-xs md:px-3 md:py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-semibold text-sm text-foreground">W{w.worker_id + 1}</span>
                    <div className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                  </div>
                  <div className="space-y-0.5 text-xs leading-tight">
                    <div className={isActive ? 'text-green-500/80' : isStarting ? 'text-amber-500/80' : 'text-muted-foreground'}>
                      {STATE_LABELS[w.browser_state] || STATE_LABELS.unknown}
                    </div>
                    {w.state_detail?.step && <div className="truncate text-foreground">{w.state_detail.step}</div>}
                    {w.state_detail?.profile && <div className="truncate text-muted-foreground">{w.state_detail.profile}</div>}
                    <div className="tabular-nums text-muted-foreground">{formatUptime(w.uptime_sec)}</div>
                    {w.respawns > 0 && <div className="text-amber-500 tabular-nums">↻ {w.respawns}</div>}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="text-xs py-4 text-center rounded-lg border border-dashed border-border text-muted-foreground">
          Все потоки остановлены
        </div>
      )}

      {selectedWorker && (() => {
        const w = workers.find(x => x.worker_id === selectedWorker.worker_id)
        return (
          <VncViewer
            workerId={selectedWorker.worker_id}
            display={selectedWorker.display}
            browserState={w?.browser_state || 'unknown'}
            onClose={() => setSelectedWorker(null)}
          />
        )
      })()}
    </div>
  )
}
